import React, { useEffect, useState } from 'react';
import { TrendingUp, TrendingDown, DollarSign, Download, AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { formatCurrency, formatDate } from '@/utils/helpers';
import api from '@/utils/api';
import { toast } from 'sonner';

export default function FluxoCaixa() {
  const [movimentos, setMovimentos] = useState([]);
  const [config, setConfig] = useState({ saldo_inicial: 0 });
  const [projecao, setProjecao] = useState(null);
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const [saldoInicial, setSaldoInicial] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [configRes, movimentosRes, projecaoRes] = await Promise.all([
        api.get('/advanced/fluxo-caixa/config'),
        api.get('/advanced/fluxo-caixa'),
        api.get('/advanced/fluxo-caixa/projecao?dias=30')
      ]);
      setConfig(configRes.data);
      setSaldoInicial(configRes.data.saldo_inicial);
      setMovimentos(movimentosRes.data);
      setProjecao(projecaoRes.data);
    } catch (error) {
      toast.error('Erro ao carregar fluxo de caixa');
    }
  };

  const handleSincronizar = async () => {
    setLoading(true);
    try {
      const response = await api.post('/advanced/fluxo-caixa/sincronizar');
      toast.success(`${response.data.total_movimentos} movimentos sincronizados`);
      loadData();
    } catch (error) {
      toast.error('Erro ao sincronizar');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateConfig = async () => {
    try {
      await api.put('/advanced/fluxo-caixa/config', null, {
        params: { saldo_inicial: saldoInicial }
      });
      toast.success('Saldo inicial atualizado');
      setIsConfigOpen(false);
      loadData();
    } catch (error) {
      toast.error('Erro ao atualizar configuração');
    }
  };

  const saldoAtual = movimentos.length > 0 ? movimentos[movimentos.length - 1].saldo_depois : config.saldo_inicial;

  const chartData = movimentos.slice(-30).map(m => ({
    data: new Date(m.data).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
    saldo: m.saldo_depois
  }));

  return (
    <div className="space-y-6" data-testid="fluxo-caixa-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-heading font-bold tracking-tight text-slate-900">Fluxo de Caixa</h1>
          <p className="text-slate-600 mt-1">Acompanhe entradas, saídas e projeções</p>
        </div>
        <div className="flex gap-3">
          <Dialog open={isConfigOpen} onOpenChange={setIsConfigOpen}>
            <DialogTrigger asChild>
              <Button variant="outline">
                Configurar Saldo Inicial
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Configurar Saldo Inicial</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Saldo Inicial (R$)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={saldoInicial}
                    onChange={(e) => setSaldoInicial(parseFloat(e.target.value))}
                  />
                </div>
                <Button onClick={handleUpdateConfig} className="w-full">
                  Salvar
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          <Button onClick={handleSincronizar} disabled={loading}>
            <RefreshCw className="h-4 w-4 mr-2" />
            {loading ? 'Sincronizando...' : 'Sincronizar'}
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
          <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center text-primary mb-4">
            <DollarSign className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-slate-500 uppercase mb-1">Saldo Atual</p>
          <p className="text-3xl font-bold text-slate-900">{formatCurrency(saldoAtual)}</p>
        </div>

        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
          <div className="h-12 w-12 rounded-full bg-accent/10 flex items-center justify-center text-accent mb-4">
            <TrendingUp className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-slate-500 uppercase mb-1">Saldo Projetado (30 dias)</p>
          <p className={`text-3xl font-bold ${projecao?.saldo_projetado >= 0 ? 'text-success' : 'text-error'}`}>
            {formatCurrency(projecao?.saldo_projetado || 0)}
          </p>
        </div>

        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
          <div className="h-12 w-12 rounded-full bg-secondary/10 flex items-center justify-center text-secondary mb-4">
            <TrendingDown className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-slate-500 uppercase mb-1">Total de Movimentos</p>
          <p className="text-3xl font-bold text-slate-900">{movimentos.length}</p>
        </div>
      </div>

      {/* Alertas */}
      {projecao?.alertas && projecao.alertas.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          {projecao.alertas.map((alerta, index) => (
            <div key={index} className="flex items-center gap-3 text-red-800">
              <AlertTriangle className="h-5 w-5" />
              <p className="font-medium">{alerta.mensagem}</p>
            </div>
          ))}
        </div>
      )}

      {/* Gráfico */}
      <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm">
        <h3 className="text-lg font-heading font-semibold text-slate-900 mb-4">Evolução do Saldo (Últimos 30 dias)</h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="data" stroke="#64748b" />
            <YAxis stroke="#64748b" />
            <Tooltip formatter={(value) => formatCurrency(value)} />
            <Line type="monotone" dataKey="saldo" stroke="#6C3483" strokeWidth={3} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Tabela de Movimentos */}
      <div className="bg-white rounded-lg border border-slate-100 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-100">
          <h3 className="text-lg font-heading font-semibold text-slate-900">Movimentos Recentes</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50/50 border-b border-slate-100">
              <tr>
                <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Data</th>
                <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Tipo</th>
                <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Descrição</th>
                <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Categoria</th>
                <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Valor</th>
                <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Saldo</th>
              </tr>
            </thead>
            <tbody>
              {movimentos.slice().reverse().slice(0, 50).map((mov) => (
                <tr key={mov.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                  <td className="p-4 text-sm text-slate-600">{formatDate(mov.data)}</td>
                  <td className="p-4 text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      mov.tipo === 'entrada' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {mov.tipo === 'entrada' ? 'Entrada' : 'Saída'}
                    </span>
                  </td>
                  <td className="p-4 text-sm text-slate-900">{mov.descricao}</td>
                  <td className="p-4 text-sm text-slate-600">{mov.categoria || '-'}</td>
                  <td className={`p-4 text-sm font-mono font-bold ${
                    mov.tipo === 'entrada' ? 'text-success' : 'text-error'
                  }`}>
                    {mov.tipo === 'entrada' ? '+' : '-'} {formatCurrency(mov.valor)}
                  </td>
                  <td className="p-4 text-sm font-mono font-bold text-slate-900">
                    {formatCurrency(mov.saldo_depois)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}